import { useState, useRef, useEffect } from "react";
import { FiSend, FiX, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import styles from "./VoiceRecorder.module.css";

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const VoiceRecorder = ({ onSend, onCancel }) => {
  const [phase,        setPhase]        = useState("recording");
  const [seconds,      setSeconds]      = useState(0);
  const [audioBlob,    setAudioBlob]    = useState(null);
  const [audioUrl,     setAudioUrl]     = useState(null);
  const [realDuration, setRealDuration] = useState(0);
  const [sending,      setSending]      = useState(false);

  const mediaRecorderRef = useRef(null);
  const streamRef        = useRef(null);
  const chunksRef        = useRef([]);
  const timerRef         = useRef(null);
  const startTimeRef     = useRef(null);
  // Guard against StrictMode double-invoke: only one recording session active at a time
  const activeRef        = useRef(false);

  useEffect(() => {
    // StrictMode runs effect twice — skip if already active
    if (activeRef.current) return;
    activeRef.current = true;
    startRecording();

    return () => {
      activeRef.current = false;
      clearInterval(timerRef.current);
      timerRef.current = null;
      try {
        if (mediaRecorderRef.current?.state !== "inactive")
          mediaRecorderRef.current?.stop();
      } catch (_) {}
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });
      streamRef.current = stream;
      chunksRef.current = []; // always reset chunks before new recording

      const mimeType =
        MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")
          ? "audio/ogg;codecs=opus"
          : "audio/mp4";

      const mr = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data?.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        clearInterval(timerRef.current);
        timerRef.current = null;

        const exactSec = startTimeRef.current
          ? Math.floor((Date.now() - startTimeRef.current) / 1000)
          : 0;

        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url  = URL.createObjectURL(blob);

        setRealDuration(exactSec);
        setSeconds(exactSec);
        setAudioBlob(blob);
        setAudioUrl(url);
        setPhase("preview");
        stream.getTracks().forEach((t) => t.stop());
      };

      // Use 100ms timeslice — safe middle ground:
      // - Gives multiple chunks so blob is complete even if stop() is called early
      // - Small enough that webm container timestamps stay accurate
      // - Avoids the "no timeslice = empty ondataavailable on some browsers" bug
      mr.start(100);
      startTimeRef.current = Date.now();
      setPhase("recording");
      setSeconds(0);

      timerRef.current = setInterval(() => {
        if (startTimeRef.current)
          setSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch {
      toast.error("Microphone access denied");
      onCancel();
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    if (
      mediaRecorderRef.current?.state === "recording" ||
      mediaRecorderRef.current?.state === "paused"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  const handleCancel = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
    activeRef.current = false;
    try {
      if (mediaRecorderRef.current?.state !== "inactive")
        mediaRecorderRef.current?.stop();
    } catch (_) {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    onCancel();
  };

  const handleDiscard = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setSeconds(0);
    setRealDuration(0);
    chunksRef.current = [];
    startRecording();
  };

  const handleSend = async () => {
    if (!audioBlob || sending) return;
    setSending(true);
    try {
      const reader = new FileReader();
      reader.onerror = () => { toast.error("Failed to read audio"); setSending(false); };
      reader.onloadend = async () => {
        try {
          const raw = reader.result;
          // Strip codec param from MIME header for Cloudinary — audio binary is untouched
          const forUpload = raw.replace(
            /^(data:audio\/[a-zA-Z0-9]+)[^;]*;base64,/,
            "$1;base64,"
          );
          URL.revokeObjectURL(audioUrl);
          await onSend(forUpload, realDuration || seconds);
        } catch {
          toast.error("Failed to send voice message");
          setSending(false);
        }
      };
      reader.readAsDataURL(audioBlob);
    } catch {
      toast.error("Failed to prepare audio");
      setSending(false);
    }
  };

  return (
    <div className={styles.container}>
      <button className={styles.cancelBtn} onClick={handleCancel} title="Cancel">
        <FiX size={16} />
      </button>

      {phase === "recording" && (
        <>
          <span className={styles.dot} />
          <span className={styles.timer}>{fmt(seconds)}</span>
          <div className={styles.wave}>
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} className={styles.waveBar}
                style={{ animationDelay: `${i * 0.08}s` }} />
            ))}
          </div>
          <button className={styles.stopBtn} onClick={stopRecording} title="Stop">
            <span className={styles.stopIcon} />
            <span className={styles.stopLabel}>Stop</span>
          </button>
        </>
      )}

      {phase === "preview" && (
        <>
          <span className={styles.previewIcon}>🎤</span>
          <span className={styles.previewTime}>{fmt(realDuration)}</span>
          <span className={styles.previewReady}>Ready to send</span>
          <button className={styles.discardBtn} onClick={handleDiscard} title="Re-record">
            <FiTrash2 size={14} />
          </button>
          <button className={styles.sendBtn} onClick={handleSend} disabled={sending}>
            <FiSend size={15} />
            <span>{sending ? "Sending…" : "Send"}</span>
          </button>
        </>
      )}
    </div>
  );
};

export default VoiceRecorder;
